import React, { useRef, useState } from 'react';
import { HiOutlineMail } from 'react-icons/hi';
import { PiPaperPlaneRight } from 'react-icons/pi';
import { SlSocialLinkedin } from 'react-icons/sl';
import { TbSparkles, TbArrowRight } from 'react-icons/tb';
import { Element } from 'react-scroll';
import './Contact.css';
import { motion } from 'framer-motion';
import { fadeInUp, cardVariant, formVariant } from '../../components/functions/animation';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const homepage = import.meta.env.VITE_HOMEPAGE ?? 'https://obaidullah-zeeshan.netlify.app';

  const validate = (fields) => {
    const errs = {};
    if (!fields.name?.trim()) errs.name = 'Name is required';
    if (!fields.email?.trim()) errs.email = 'Email is required';
    else if (!EMAIL_RX.test(fields.email)) errs.email = 'Enter a valid email address';
    if (!fields.message?.trim()) errs.message = 'Message cannot be empty';
    return errs;
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const handleChange = (e) => {
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    const form = formRef.current;
    const fields = {
      name: form.elements['name'].value,
      email: form.elements['email'].value,
      message: form.elements['message'].value,
    };
    const validationErrors = validate(fields);
    if (Object.keys(validationErrors).length > 0) {
      e.preventDefault();
      setErrors(validationErrors);
      setTouched({ name: true, email: true, message: true });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
  };

  return (
    <>
      <Element name="contact">
        <section id="contact">
          <div className="container">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <p className="section-label">Let's Connect</p>
              <h2 className="heading">Contact <span>Me</span></h2>
            </motion.div>

            <div className="row contact-wrapper">
              <motion.div
                className="col-lg-4 col-12 contact-card-wrap"
                variants={fadeInUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
              >
                {/* ── Card 1: Intro / featured panel ── */}
                <motion.div
                  className="cc-intro-card"
                  variants={cardVariant}
                  custom={0}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                >
                  {/* Decorative watermark */}
                  <div className="cc-watermark" aria-hidden="true">OZ</div>
                  {/* Decorative orbs */}
                  <div className="cc-orb cc-orb-1" aria-hidden="true" />
                  <div className="cc-orb cc-orb-2" aria-hidden="true" />

                  <div className="cc-avail-row">
                    <span className="cc-avail-dot" />
                    <span className="cc-avail-text">Available for work</span>
                  </div>

                  <p className="cc-intro-headline">
                    Got a project or idea?{' '}
                    <span className="cc-headline-accent">Let's make it real.</span>
                  </p>

                  <p className="cc-intro-sub">
                    Open to freelance, full-time roles &amp; exciting collaborations.
                    I typically reply within <strong>24 hours</strong>.
                  </p>

                  <div className="cc-divider" />

                  <p className="cc-intro-quote">
                    &ldquo;Great code starts with a great conversation.&rdquo;
                  </p>
                </motion.div>

                {/* ── Card 2: LinkedIn ── */}
                <motion.a
                  className="cc-link-card"
                  href="https://www.linkedin.com/in/obaidullah-zeeshan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  custom={1}
                  variants={cardVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  whileHover="hover"
                >
                  <div className="cc-card-shine" aria-hidden="true" />
                  <div className="cc-icon-circle">
                    <SlSocialLinkedin />
                  </div>
                  <div className="cc-card-body">
                    <span className="cc-card-title">LinkedIn</span>
                    <span className="cc-card-sub">Obaidullah Zeeshan</span>
                  </div>
                  <motion.div
                    className="cc-arrow"
                    variants={{ hover: { x: 4, opacity: 1 } }}
                    initial={{ opacity: 0.4 }}
                  >
                    <TbArrowRight />
                  </motion.div>
                </motion.a>

                {/* ── Card 3: Gmail ── */}
                <motion.a
                  className="cc-link-card"
                  href="mailto:obaidzeeshan.official@gmail.com"
                  custom={2}
                  variants={cardVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  whileHover="hover"
                >
                  <div className="cc-card-shine" aria-hidden="true" />
                  <div className="cc-icon-circle">
                    <HiOutlineMail />
                  </div>
                  <div className="cc-card-body">
                    <span className="cc-card-title">Gmail</span>
                    <span className="cc-card-sub">obaidzeeshan.official@gmail.com</span>
                  </div>
                  <motion.div
                    className="cc-arrow"
                    variants={{ hover: { x: 4, opacity: 1 } }}
                    initial={{ opacity: 0.4 }}
                  >
                    <TbArrowRight />
                  </motion.div>
                </motion.a>
              </motion.div>

              <motion.div
                className="col-lg-7 col-12 contact-form"
                variants={formVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <div className="form-wrapper">
                  <form
                    ref={formRef}
                    action="https://submit-form.com/oETHM3MgD"
                    method="POST"
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <input type="text" name="_honeypot" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

                    <div className="mb-4 form-field">
                      <label htmlFor="contact-name" className="form-label">Your Name</label>
                      <input
                        id="contact-name"
                        type="text"
                        className={`form-control${errors.name && touched.name ? ' is-invalid' : ''}`}
                        placeholder="e.g. John Doe"
                        name="name"
                        required
                        onBlur={handleBlur}
                        onChange={handleChange}
                        aria-describedby={errors.name && touched.name ? 'name-error' : undefined}
                      />
                      {errors.name && touched.name && (
                        <span id="name-error" className="form-error" role="alert">{errors.name}</span>
                      )}
                    </div>

                    <div className="mb-4 form-field">
                      <label htmlFor="contact-email" className="form-label">Your Email</label>
                      <input
                        id="contact-email"
                        type="email"
                        className={`form-control${errors.email && touched.email ? ' is-invalid' : ''}`}
                        placeholder="e.g. john@example.com"
                        name="email"
                        required
                        onBlur={handleBlur}
                        onChange={handleChange}
                        aria-describedby={errors.email && touched.email ? 'email-error' : undefined}
                      />
                      {errors.email && touched.email && (
                        <span id="email-error" className="form-error" role="alert">{errors.email}</span>
                      )}
                    </div>

                    <div className="mb-4 form-field">
                      <label htmlFor="contact-message" className="form-label">Your Message</label>
                      <textarea
                        id="contact-message"
                        className={`form-control${errors.message && touched.message ? ' is-invalid' : ''}`}
                        rows="6"
                        name="message"
                        placeholder="Write your message here..."
                        required
                        onBlur={handleBlur}
                        onChange={handleChange}
                        aria-describedby={errors.message && touched.message ? 'message-error' : undefined}
                      />
                      {errors.message && touched.message && (
                        <span id="message-error" className="form-error" role="alert">{errors.message}</span>
                      )}
                    </div>

                    <div className="submit-row">
                    <motion.button
                      type="submit"
                      className={`send-btn${isSubmitting ? ' loading' : ''}`}
                      disabled={isSubmitting}
                      whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                      aria-busy={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>Sending… <span className="btn-spinner" aria-hidden="true" /></>
                      ) : (
                        <>Send Message <span><PiPaperPlaneRight /></span></>
                      )}
                    </motion.button>
                    </div>

                    <input type="hidden" name="_redirect" value={`${homepage}/success`} />
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </Element>
    </>
  );
};

export default Contact;
